import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load user data on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (authService.isAuthenticated()) {
          // Try to get fresh user data
          try {
            const userData = await authService.getCurrentUser();
            const userPermissions = await authService.getPermissions();
            
            setUser(userData);
            setPermissions(userPermissions);
            setIsAuthenticated(true);
          } catch (error) {
            // If API fails, use stored data
            const storedUser = authService.getStoredUser();
            const storedPermissions = authService.getStoredPermissions();
            
            if (storedUser) {
              setUser(storedUser);
              setPermissions(storedPermissions);
              setIsAuthenticated(true);
            } else {
              // No valid data, clear everything
              await authService.logout();
              setIsAuthenticated(false);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, []);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const data = await authService.login(email, password);
      
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Load permissions
      const userPermissions = await authService.getPermissions();
      setPermissions(userPermissions);
      
      setIsLoading(false);
      toast.success(`Welcome back, ${data.user.full_name}!`);
      return data;
    } catch (error) {
      setIsLoading(false);
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setPermissions([]);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local state even if API call fails
      setUser(null);
      setPermissions([]);
      setIsAuthenticated(false);
    }
  };

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (perms) => {
    return perms.some((perm) => permissions.includes(perm));
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isReviewer = () => {
    return user?.role === 'reviewer';
  };

  const value = {
    user,
    permissions,
    isLoading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isAdmin,
    isReviewer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
